<template>
  <div class="file-upload-component">
    <!-- Dropzone -->
    <div
      class="dropzone"
      :class="{
        'dropzone-active': isDragging,
        'dropzone-disabled': disabled || uploading
      }"
      @drop.prevent="onDrop"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @click="openFileDialog"
    >
      <div v-if="!uploading" class="dropzone-content">
        <b-icon icon="cloud-upload" font-scale="3" variant="secondary"></b-icon>
        <p class="mt-3 mb-2">
          <strong>Drop files here or click to browse</strong>
        </p>
        <p class="text-muted small">
          {{ acceptedTypesText }} • Max {{ maxFileSize }}MB per file • Max {{ maxFiles }} files
        </p>
      </div>
      <div v-else class="dropzone-content">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-3">Uploading {{ uploadProgress }}%</p>
      </div>
    </div>

    <!-- File input (hidden) -->
    <input
      ref="fileInput"
      type="file"
      :accept="acceptedTypes"
      :multiple="allowMultiple"
      style="display: none"
      @change="onFileSelected"
    />

    <!-- Error messages -->
    <b-alert
      v-if="errorMessage"
      variant="danger"
      dismissible
      @dismissed="errorMessage = null"
      class="mt-3"
    >
      {{ errorMessage }}
    </b-alert>

    <!-- File list -->
    <div v-if="fileList.length > 0" class="file-list mt-3">
      <h6>{{ uploadedFiles ? 'Uploaded Files' : 'Selected Files' }}</h6>
      <b-list-group>
        <b-list-group-item
          v-for="file in fileList"
          :key="file.id || file.name"
          class="d-flex justify-content-between align-items-center"
        >
          <div class="file-info">
            <b-icon
              :icon="getFileIcon(file)"
              class="mr-2"
              :variant="file.uploaded ? 'success' : 'secondary'"
            ></b-icon>
            <span>{{ file.filename || file.name }}</span>
            <span class="text-muted ml-2">({{ formatFileSize(file.file_size || file.size) }})</span>
          </div>
          <div class="file-actions">
            <b-button
              v-if="file.uploaded && canPreview(file)"
              size="sm"
              variant="outline-primary"
              @click="previewFile(file)"
              class="mr-2"
            >
              <b-icon icon="eye"></b-icon> Preview
            </b-button>
            <b-button
              v-if="file.uploaded"
              size="sm"
              variant="outline-primary"
              @click="downloadFile(file)"
              class="mr-2"
            >
              <b-icon icon="download"></b-icon> Download
            </b-button>
            <b-button
              v-if="allowDelete && (file.uploaded || !autoUpload)"
              size="sm"
              variant="outline-danger"
              @click="deleteFile(file)"
              :disabled="uploading"
            >
              <b-icon icon="trash"></b-icon>
            </b-button>
          </div>
        </b-list-group-item>
      </b-list-group>
    </div>

    <!-- Manual upload button (if autoUpload is false) -->
    <div v-if="!autoUpload && selectedFiles.length > 0 && !uploadedFiles" class="mt-3">
      <b-button variant="primary" @click="uploadFiles" :disabled="uploading">
        <b-spinner v-if="uploading" small></b-spinner>
        <span v-else>Upload {{ selectedFiles.length }} file(s)</span>
      </b-button>
    </div>

    <!-- Preview Modal -->
    <b-modal
      id="file-upload-preview-modal"
      :title="previewTitle"
      size="xl"
      ok-only
      ok-title="Close"
    >
      <div v-if="previewType === 'pdf'">
        <iframe :src="previewUrl" style="width: 100%; height: 80vh; border: none;"></iframe>
      </div>
      <div v-else-if="previewType === 'image'" class="text-center">
        <img :src="previewUrl" style="max-width: 100%; max-height: 80vh;" alt="Preview" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FileUpload',
  props: {
    category: {
      type: String,
      required: true,
      validator: (value) => ['sigdef_response', 'ccr', 'lsli', 'soc_waiver', 'asbestos_waiver'].includes(value),
    },
    entityId: {
      type: Number,
      default: null,
    },
    maxFiles: {
      type: Number,
      default: 5,
    },
    maxFileSize: {
      type: Number,
      default: 10, // MB
    },
    acceptedTypes: {
      type: String,
      default: '.pdf,.jpg,.jpeg,.png,.docx',
    },
    allowMultiple: {
      type: Boolean,
      default: true,
    },
    allowDelete: {
      type: Boolean,
      default: true,
    },
    autoUpload: {
      type: Boolean,
      default: true,
    },
    uploadedFiles: {
      type: Array,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      selectedFiles: [],
      uploadedFilesList: [],
      uploading: false,
      uploadProgress: 0,
      errorMessage: null,
      previewUrl: null,
      previewType: null,
      previewTitle: '',
    };
  },
  computed: {
    fileList() {
      // Show uploaded files if provided as prop, or track uploaded files internally
      if (this.uploadedFiles && this.uploadedFiles.length > 0) {
        return this.uploadedFiles.map((file) => ({ ...file, uploaded: true }));
      }
      // Show internally tracked uploaded files
      if (this.uploadedFilesList.length > 0) {
        return this.uploadedFilesList.map((file) => ({ ...file, uploaded: true }));
      }
      // Otherwise show selected files awaiting upload
      return this.selectedFiles;
    },
    acceptedTypesText() {
      const types = this.acceptedTypes.split(',').map((t) => t.trim().toUpperCase());
      return types.join(', ');
    },
  },
  methods: {
    openFileDialog() {
      if (this.disabled || this.uploading) return;
      this.$refs.fileInput.click();
    },
    onFileSelected(event) {
      const { files } = event.target;
      this.handleFiles(files);
      // Reset input
      const input = event.target;
      input.value = '';
    },
    onDragOver() {
      if (this.disabled || this.uploading) return;
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(event) {
      if (this.disabled || this.uploading) return;
      this.isDragging = false;
      const { files } = event.dataTransfer;
      this.handleFiles(files);
    },
    handleFiles(files) {
      this.errorMessage = null;

      // Convert FileList to Array
      const fileArray = Array.from(files);

      // Validate file count
      const totalFiles = this.selectedFiles.length + fileArray.length;
      if (totalFiles > this.maxFiles) {
        this.errorMessage = `Maximum ${this.maxFiles} files allowed`;
        return;
      }

      // Validate each file
      const validFiles = [];
      for (let i = 0; i < fileArray.length; i += 1) {
        const file = fileArray[i];

        // Validate file size
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > this.maxFileSize) {
          this.errorMessage = `File ${file.name} exceeds ${this.maxFileSize}MB`;
          // eslint-disable-next-line no-console
          console.error(`File size validation failed: ${fileSizeMB}MB > ${this.maxFileSize}MB`);
          return;
        }

        // Validate file type
        const fileExt = `.${file.name.split('.').pop().toLowerCase()}`;
        const acceptedTypesArray = this.acceptedTypes.split(',');
        if (!acceptedTypesArray.includes(fileExt)) {
          this.errorMessage = `File type ${fileExt} not allowed. Accepted: ${this.acceptedTypes}`;
          // eslint-disable-next-line no-console
          console.error(`File type validation failed: ${fileExt} not in ${this.acceptedTypes}`);
          return;
        }

        validFiles.push(file);
      }

      // eslint-disable-next-line no-console
      console.log(`${validFiles.length} files validated successfully`);

      // Add valid files to selected list
      this.selectedFiles.push(...validFiles);

      // Emit event
      this.$emit('files-selected', validFiles);

      // Auto-upload if enabled
      if (this.autoUpload) {
        // eslint-disable-next-line no-console
        console.log('Auto-upload enabled, starting upload...');
        this.uploadFiles();
      }
    },
    async uploadFiles() {
      if (this.selectedFiles.length === 0) return;

      this.uploading = true;
      this.uploadProgress = 0;
      this.errorMessage = null;

      try {
        const formData = new FormData();

        // Add files
        this.selectedFiles.forEach((file) => {
          formData.append('files', file);
        });

        // Add metadata
        formData.append('category', this.category);
        if (this.entityId) {
          formData.append('entity_id', this.entityId);
        }

        // Upload
        const response = await axios.post('/api/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
          },
        });

        // Success - store uploaded files and clear selected
        this.uploadedFilesList.push(...response.data.files);
        this.selectedFiles = [];
        this.$emit('upload-complete', response.data.files);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Upload error:', error);
        this.errorMessage = (error.response && error.response.data && error.response.data.error) || 'Upload failed. Please try again.';
        this.$emit('upload-error', error);
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
      }
    },
    async deleteFile(file) {
      if (!file.uploaded) {
        // Remove from selected files (not yet uploaded)
        const index = this.selectedFiles.findIndex((f) => f.name === file.name);
        if (index > -1) {
          this.selectedFiles.splice(index, 1);
        }
        return;
      }

      // Delete uploaded file
      try {
        await axios.delete(`/api/files/${file.id}`);

        // Remove from uploadedFilesList if we're tracking it internally
        const index = this.uploadedFilesList.findIndex((f) => f.id === file.id);
        if (index > -1) {
          this.uploadedFilesList.splice(index, 1);
        }

        this.$emit('file-deleted', file);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Delete error:', error);
        this.errorMessage = (error.response && error.response.data && error.response.data.error) || 'Delete failed. Please try again.';
      }
    },
    getFileIcon(file) {
      const filename = file.filename || file.name;
      const ext = filename.split('.').pop().toLowerCase();

      switch (ext) {
        case 'pdf':
          return 'file-earmark-pdf';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'file-earmark-image';
        case 'docx':
        case 'doc':
          return 'file-earmark-word';
        default:
          return 'file-earmark';
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    },
    downloadFile(file) {
      // Open download endpoint in new window
      window.open(`/api/files/${file.id}/download`, '_blank');
    },
    reset() {
      this.selectedFiles = [];
      this.uploadedFilesList = [];
      this.errorMessage = null;
      this.uploadProgress = 0;
    },
    canPreview(file) {
      const mimeType = file.mime_type || '';
      return mimeType === 'application/pdf'
        || mimeType.startsWith('image/');
    },
    previewFile(file) {
      const mimeType = file.mime_type || '';

      // Check if mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // On mobile, or for PDFs on mobile, just download instead of preview
      if (isMobile && mimeType === 'application/pdf') {
        // Open download in new tab for mobile
        window.open(`/api/files/${file.id}/download`, '_blank');
        return;
      }

      // Desktop preview
      this.previewTitle = file.filename;
      this.previewUrl = `/api/files/${file.id}/preview`;

      if (mimeType === 'application/pdf') {
        this.previewType = 'pdf';
      } else if (mimeType.startsWith('image/')) {
        this.previewType = 'image';
      }

      this.$bvModal.show('file-upload-preview-modal');
    },
  },
};
</script>

<style scoped>
.dropzone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;
}

.dropzone:hover:not(.dropzone-disabled) {
  border-color: #007bff;
  background-color: #f0f8ff;
}

.dropzone-active {
  border-color: #007bff;
  background-color: #e6f2ff;
  transform: scale(1.02);
}

.dropzone-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.dropzone-content {
  pointer-events: none;
}

.file-list {
  max-height: 400px;
  overflow-y: auto;
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.file-actions {
  margin-left: 10px;
}
</style>
